import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ScheduleService } from '../services/scheduleService';
import { RouteService } from '../services/routeService';
import { BusService } from '../services/busService';
import { BusSchedule, Route, Bus } from '../types';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';

export const Schedule: React.FC = () => {
    const [view, setView] = useState<'day' | 'week' | 'month'>('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [schedules, setSchedules] = useState<BusSchedule[]>([]);
    const [routes, setRoutes] = useState<Route[]>([]);
    const [buses, setBuses] = useState<Bus[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Form state
    const [selectedRoute, setSelectedRoute] = useState('');
    const [selectedBus, setSelectedBus] = useState('');
    const [departureDate, setDepartureDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [departureTime, setDepartureTime] = useState('08:00');
    const [duration, setDuration] = useState('4'); // hours

    useEffect(() => {
        loadData();
    }, [currentDate, view]);

    const loadData = async () => {
        setLoading(true);
        try {
            let start, end;
            if (view === 'day') {
                start = new Date(currentDate.setHours(0, 0, 0, 0));
                end = new Date(currentDate.setHours(23, 59, 59, 999));
            } else if (view === 'week') {
                start = startOfWeek(currentDate);
                end = endOfWeek(currentDate);
            } else {
                start = startOfMonth(currentDate);
                end = endOfMonth(currentDate);
            }

            // Load each piece of data independently to prevent one failure from blocking others
            const fetchSchedules = async () => {
                try {
                    const data = await ScheduleService.getSchedules(start, end);
                    setSchedules(data);
                } catch (error) {
                    console.error('Failed to load schedules:', error);
                }
            };

            const fetchRoutes = async () => {
                try {
                    const data = await RouteService.getRoutes(100);
                    setRoutes(data);
                } catch (error) {
                    console.error('Failed to load routes:', error);
                }
            };

            const fetchBuses = async () => {
                try {
                    const data = await BusService.getBuses(100);
                    setBuses(data);
                } catch (error) {
                    console.error('Failed to load buses:', error);
                }
            };

            await Promise.allSettled([
                fetchSchedules(),
                fetchRoutes(),
                fetchBuses()
            ]);

        } catch (error) {
            console.error('Unexpected error in loadData:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrev = () => {
        if (view === 'day') setCurrentDate(subDays(currentDate, 1));
        else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
        else setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNext = () => {
        if (view === 'day') setCurrentDate(addDays(currentDate, 1));
        else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
        else setCurrentDate(addMonths(currentDate, 1));
    };

    const handleCreateSchedule = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const depTime = new Date(`${departureDate}T${departureTime}`);
            const arrTime = new Date(depTime.getTime() + parseInt(duration) * 60 * 60 * 1000);

            await ScheduleService.createSchedule({
                routeId: selectedRoute,
                busId: selectedBus,
                departureTime: depTime,
                arrivalTime: arrTime,
            });

            setShowModal(false);
            loadData();
        } catch (error) {
            alert('Failed to create schedule');
        }
    };

    const renderHeader = () => (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
                <h1 className="text-2xl font-bold text-secondary-900">Bus Schedules</h1>
                <p className="text-secondary-600">Plan and manage bus operations</p>
            </div>
            <div className="flex items-center space-x-3">
                <div className="flex bg-secondary-100 rounded-lg p-1">
                    <button
                        onClick={() => setView('day')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${view === 'day' ? 'bg-white shadow-sm text-primary-600' : 'text-secondary-600'}`}
                    >
                        Day
                    </button>
                    <button
                        onClick={() => setView('week')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${view === 'week' ? 'bg-white shadow-sm text-primary-600' : 'text-secondary-600'}`}
                    >
                        Week
                    </button>
                    <button
                        onClick={() => setView('month')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${view === 'month' ? 'bg-white shadow-sm text-primary-600' : 'text-secondary-600'}`}
                    >
                        Month
                    </button>
                </div>
                <Button onClick={() => setShowModal(true)} className="flex items-center">
                    <Plus className="h-4 w-4 mr-2" />
                    Schedule Bus
                </Button>
            </div>
        </div>
    );

    const renderControls = () => (
        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-secondary-200">
            <div className="flex items-center space-x-4">
                <button onClick={handlePrev} className="p-2 hover:bg-secondary-50 rounded-full">
                    <ChevronLeft className="h-5 w-5 text-secondary-600" />
                </button>
                <h2 className="text-lg font-semibold text-secondary-900">
                    {view === 'day' ? format(currentDate, 'MMMM d, yyyy') :
                        view === 'week' ? `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}` :
                            format(currentDate, 'MMMM yyyy')}
                </h2>
                <button onClick={handleNext} className="p-2 hover:bg-secondary-50 rounded-full">
                    <ChevronRight className="h-5 w-5 text-secondary-600" />
                </button>
            </div>
            <Button variant="outline" onClick={() => setCurrentDate(new Date())}>Today</Button>
        </div>
    );

    const renderDayView = () => (
        <div className="space-y-4">
            {schedules.length === 0 ? (
                <Card className="p-8 text-center text-secondary-500">No buses scheduled for today</Card>
            ) : (
                schedules.map(schedule => {
                    const route = routes.find(r => r.id === schedule.routeId);
                    const bus = buses.find(b => b.id === schedule.busId);
                    return (
                        <Card key={schedule.id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="bg-primary-50 p-3 rounded-lg text-primary-600 font-bold">
                                    {format(schedule.departureTime, 'HH:mm')}
                                </div>
                                <div>
                                    <h3 className="font-semibold">{route ? `${route.fromCity} → ${route.toCity}` : 'Unknown Route'}</h3>
                                    <p className="text-sm text-secondary-600">{bus ? `${bus.busNumber} (${bus.operatorName})` : 'Unknown Bus'}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${schedule.status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                                    schedule.status === 'active' ? 'bg-green-50 text-green-600' :
                                        'bg-secondary-50 text-secondary-600'
                                    }`}>
                                    {schedule.status.toUpperCase()}
                                </div>
                                <Button variant="outline" size="sm">Details</Button>
                            </div>
                        </Card>
                    );
                })
            )}
        </div>
    );

    const renderWeekView = () => {
        const days = eachDayOfInterval({
            start: startOfWeek(currentDate),
            end: endOfWeek(currentDate)
        });

        return (
            <div className="grid grid-cols-7 gap-4">
                {days.map((day: Date) => (
                    <div key={day.toString()} className="space-y-2">
                        <div className={`text-center py-2 rounded-lg font-medium ${isSameDay(day, new Date()) ? 'bg-primary-600 text-white' : 'bg-white text-secondary-900 border border-secondary-200'}`}>
                            <div className="text-xs uppercase">{format(day, 'EEE')}</div>
                            <div className="text-lg">{format(day, 'd')}</div>
                        </div>
                        <div className="space-y-2">
                            {schedules.filter(s => isSameDay(s.departureTime, day)).map(s => {
                                const route = routes.find(r => r.id === s.routeId);
                                return (
                                    <div key={s.id} className="p-2 bg-white border border-secondary-200 rounded-lg text-xs shadow-sm">
                                        <div className="font-bold text-primary-600">{format(s.departureTime, 'HH:mm')}</div>
                                        <div className="truncate font-medium">{route ? `${route.fromCity} → ${route.toCity}` : 'Route'}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderMonthView = () => {
        // Basic calendar implementation
        const days = eachDayOfInterval({
            start: startOfMonth(currentDate),
            end: endOfMonth(currentDate)
        });

        return (
            <Card className="p-4 grid grid-cols-7 gap-px bg-secondary-200">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="bg-white p-2 text-center text-xs font-bold text-secondary-500 uppercase">{d}</div>
                ))}
                {days.map((day: Date) => (
                    <div key={day.toString()} className={`min-h-[100px] bg-white p-2 border-t border-secondary-100 ${!isSameDay(day, currentDate) ? 'text-secondary-400' : 'text-primary-600 font-bold'}`}>
                        <div className="text-right text-xs mb-1">{format(day, 'd')}</div>
                        <div className="space-y-1">
                            {schedules.filter(s => isSameDay(s.departureTime, day)).slice(0, 3).map(s => (
                                <div key={s.id} className="text-[10px] bg-primary-50 text-primary-700 px-1 rounded truncate">
                                    {format(s.departureTime, 'HH:mm')} Trip
                                </div>
                            ))}
                            {schedules.filter(s => isSameDay(s.departureTime, day)).length > 3 && (
                                <div className="text-[10px] text-secondary-500 text-center font-medium">
                                    + {schedules.filter(s => isSameDay(s.departureTime, day)).length - 3} more
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            {renderHeader()}
            {renderControls()}

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {view === 'day' && renderDayView()}
                    {view === 'week' && renderWeekView()}
                    {view === 'month' && renderMonthView()}
                </>
            )}

            {/* Scheduling Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <Card className="w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold mb-4">Schedule New Bus</h2>
                        <form onSubmit={handleCreateSchedule} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Route</label>
                                <select
                                    required
                                    value={selectedRoute}
                                    onChange={(e) => setSelectedRoute(e.target.value)}
                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">Select a route</option>
                                    {routes.map(r => (
                                        <option key={r.id} value={r.id}>{r.fromCity} → {r.toCity} ({r.departureTime})</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Bus</label>
                                <select
                                    required
                                    value={selectedBus}
                                    onChange={(e) => setSelectedBus(e.target.value)}
                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">Select a bus</option>
                                    {buses.map(b => (
                                        <option key={b.id} value={b.id}>{b.busNumber} - {b.operatorName} ({b.busType.name})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={departureDate}
                                        onChange={(e) => setDepartureDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Time</label>
                                    <input
                                        type="time"
                                        required
                                        value={departureTime}
                                        onChange={(e) => setDepartureTime(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Estimated Duration (hours)</label>
                                <input
                                    type="number"
                                    required
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <Button variant="outline" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
                                <Button type="submit">Create Schedule</Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
};
